// frontend/src/components/CostEstimation/EstimationTable.jsx
import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { Table, Select, InputNumber, Tooltip, Input, Button } from 'antd';
import {
  InfoCircleOutlined, LockOutlined, WarningOutlined, CheckOutlined, SearchOutlined
} from '@ant-design/icons';
import {
  DEFAULT_EXPAND_LEVEL, expandedCodesForLevel,
  isRowVisible, buildGroupSummaries, rowsMatching, matchesQuery, buildAncestorIndex
} from '../../services/estimationCalc';
import {
  formatSuggestionLabel,
  formatNumber,
  calculateRowTotal,
  inflationFactorForSuggestion,
  suggestionYear
} from '../../services/estimationService';

const { Option } = Select;

/**
 * Check if a row has been manually edited by the user
 * Returns true if user has actually entered/changed values
 * Returns false if values are still at defaults (untouched)
 */
const isManuallyEdited = (row) => {
  // Check if user selected a project
  const hasProjectSelected = row.selectedSuggestionIndex !== null &&
                             row.selectedSuggestionIndex !== undefined;

  // Check if user manually entered costPerUnit
  const hasManualCostPerUnit = row.manualCostPerUnit !== null &&
                                row.manualCostPerUnit !== undefined &&
                                row.manualCostPerUnit !== 0;

  // Check if user manually entered amount (non-zero)
  const hasManualAmount = row.manualAmount !== null &&
                         row.manualAmount !== undefined &&
                         row.manualAmount !== 0;

  // Row is manually edited if ANY of these conditions are true
  return hasProjectSelected || hasManualCostPerUnit || hasManualAmount;
};

/**
 * Get effective values for a row
 *
 * FIXED LOGIC:
 * - For parent rows NOT manually edited: Use row.calculatedTotal (sum of children)
 * - For manually edited rows: Calculate from amount × costPerUnit
 */
const getEffectiveValues = (row) => {
  // Level 1 nodes: show calculated total only
  if (row.level === 1) {
    return { amount: null, costPerUnit: null, total: row.calculatedTotal || 0 };
  }

  // Level 2+ nodes: check if manually edited
  const manuallyEdited = isManuallyEdited(row);
  const isParent = !row.isLeaf;

  // Parent row NOT manually edited: use calculatedTotal from children sum
  if (isParent && !manuallyEdited) {
    return { amount: null, costPerUnit: null, total: row.calculatedTotal || 0 };
  }

  // Leaf row OR manually edited parent: calculate from manual values
  const amount = row.manualAmount !== null && row.manualAmount !== undefined
    ? row.manualAmount
    : 0;

  let costPerUnit = 0;

  if (row.manualCostPerUnit !== null && row.manualCostPerUnit !== undefined) {
    costPerUnit = row.manualCostPerUnit;
  } else if (row.selectedSuggestionIndex !== null &&
             row.selectedSuggestionIndex !== undefined &&
             row.suggestions &&
             row.suggestions[row.selectedSuggestionIndex]) {
    // Search-derived price: indexed from the project's year to the latest data point
    const suggestion = row.suggestions[row.selectedSuggestionIndex];
    costPerUnit = (suggestion.costPerUnit || 0) * inflationFactorForSuggestion(row, suggestion);
  }

  const total = calculateRowTotal(amount, costPerUnit);

  return { amount, costPerUnit, total };
};

// Hoisted so the Table props keep a stable identity between renders.
const TABLE_SCROLL = { x: 1700, y: 'calc(100vh - 400px)' };
const TABLE_EXPANDABLE = { expandedRowKeys: [], showExpandColumn: false };

/**
 * A cell only ever renders from its own record plus the stable callbacks
 * below, so it can be skipped whenever the record object is unchanged.
 * Row recalculation returns a new object for every row whose values changed
 * and keeps the identity of the rest (see services/estimationCalc.js), which
 * is what turns a full-table re-render into a handful of cells.
 * Note: this relies on the row order being fixed, which it is — rows only
 * change wholesale, on load.
 */
const shouldCellUpdate = (record, prevRecord) => record !== prevRecord;

// Pure: derived only from the record, so it is hoisted out of the component.
const rowClassName = (record) => {
  // Add custom row classes for styling
  const classes = [];

  if (record.level === 1) {
    classes.push('estimation-row-level-1');
    classes.push('estimation-row-parent');
  } else if (!record.isLeaf) {
    classes.push('estimation-row-parent');
  } else {
    classes.push('estimation-row-leaf');
  }

  classes.push(`estimation-row-level-${record.level}`);

  if (record.hasInconsistency) {
    classes.push('estimation-row-inconsistent');
  }

  if (record.isBookmarked) {
    classes.push('estimation-row-bookmarked');
  }

  return classes.join(' ');
};

const EstimationTable = ({ rows, onRowChange, onToggleAll, storageKey, searchResultLimit = 5 }) => {
  // --- collapse / find state ---------------------------------------------
  const [expandedCodes, setExpandedCodes] = useState(() => new Set());
  const [query, setQuery] = useState('');
  const [hitLimit, setHitLimit] = useState(searchResultLimit);
  const [initialised, setInitialised] = useState(false);

  // Keeps the identity of decorated group rows stable between renders, so a
  // group only re-renders when its own row, its expansion or its roll-up
  // actually changed.
  const decorated = useRef(new Map());

  // Which ancestors each row hangs under on screen.
  const ancestorIndex = useMemo(() => buildAncestorIndex(rows), [rows]);

  // Roll-ups for the collapsed rows: what each subtree contains.
  const summaryCache = useRef(new Map());
  const summaries = useMemo(() => {
    const fresh = buildGroupSummaries(rows);
    const prev = summaryCache.current;
    const stable = new Map();
    fresh.forEach((value, code) => {
      const old = prev.get(code);
      const same = old &&
        old.positions === value.positions &&
        old.withReference === value.withReference &&
        old.filled === value.filled &&
        old.warning === value.warning &&
        old.bookmarked === value.bookmarked;
      stable.set(code, same ? old : value);
    });
    summaryCache.current = stable;
    return stable;
  }, [rows]);

  // Open to level 1-2 the first time this estimation is shown, or restore the
  // expansion the user last left it at.
  useEffect(() => {
    if (initialised || rows.length === 0) return;
    let restored = null;
    try {
      const raw = storageKey && window.localStorage.getItem('perita.expanded.' + storageKey);
      if (raw) {
        const codes = JSON.parse(raw);
        if (Array.isArray(codes) && codes.length > 0) restored = new Set(codes);
      }
    } catch (e) { /* private mode / blocked storage: fall back to the default */ }
    setExpandedCodes(restored || expandedCodesForLevel(rows, DEFAULT_EXPAND_LEVEL));
    setInitialised(true);
  }, [rows, storageKey, initialised]);

  useEffect(() => {
    if (!initialised || !storageKey) return;
    try {
      window.localStorage.setItem('perita.expanded.' + storageKey,
                                  JSON.stringify([...expandedCodes]));
    } catch (e) { /* not worth failing an edit over */ }
  }, [expandedCodes, storageKey, initialised]);

  // Every new query (and a changed setting) starts from the first batch again.
  useEffect(() => { setHitLimit(searchResultLimit); }, [query, searchResultLimit]);

  const toggleExpanded = useCallback((code) => {
    setExpandedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }, []);

  // --- which rows actually get rendered -----------------------------------
  // A search or a filter overrides the collapse state and shows every hit in
  // context (its ancestors come along), so nothing has to be hunted for.
  const isFinding = Boolean(query.trim());

  const { displayRows, matchCount, shownCount } = useMemo(() => {
    let base, matched = 0, shown = 0;
    if (isFinding) {
      const found = rowsMatching(rows, (r) => matchesQuery(r, query), ancestorIndex, hitLimit);
      base = found.rows;
      matched = found.matchCount;
      shown = found.shownCount;
    } else {
      base = rows.filter((r) => isRowVisible(r, expandedCodes, ancestorIndex));
    }

    // Attach the collapse state and the roll-up so the Element cell can draw
    // them; shouldCellUpdate keys on record identity, so a row only re-renders
    // when its own data, its expansion or its roll-up changed.
    const cache = decorated.current;
    const next = new Map();

    const out = base.map((row) => {
      const summary = summaries.get(row.ebkpCode);
      if (!summary) return row;                       // leaf: nothing to attach

      const expanded = isFinding ? true : expandedCodes.has(row.ebkpCode);
      const prev = cache.get(row.ebkpCode);
      if (prev &&
          prev.__row === row &&
          prev.__expanded === expanded &&
          prev.__summary === summary) {
        next.set(row.ebkpCode, prev);
        return prev;
      }

      const decoratedRow = {
        ...row,
        __row: row,
        __expandable: true,
        __expanded: expanded,
        __summary: summary
      };
      next.set(row.ebkpCode, decoratedRow);
      return decoratedRow;
    });

    decorated.current = next;
    return { displayRows: out, matchCount: matched, shownCount: shown };
  }, [rows, expandedCodes, summaries, ancestorIndex, isFinding, query, hitLimit]);

  /**
   * Handle toggle inclusion for a single row
   */
  const handleToggleInclusion = useCallback((row) => {
    onRowChange(row.ebkpCode, {
      includedInTotal: !row.includedInTotal
    });
  }, [onRowChange]);

  /**
   * Handle toggle bookmark for a single row
   */
  const handleToggleBookmark = useCallback((row) => {
    onRowChange(row.ebkpCode, {
      isBookmarked: !row.isBookmarked
    });
  }, [onRowChange]);

  /**
   * Handle change in selected project
   * Level 1: Not allowed (read-only)
   * Level 2+: Allowed (ALWAYS, even if parent has children)
   * Bookmarked rows: Not allowed (locked)
   *
   * UPDATED: Only affects costPerUnit, NOT amount (decoupled)
   */
  const handleProjectChange = useCallback((row, suggestionIndex) => {
    // Level 1 is read-only
    if (row.level === 1) return;

    // Bookmarked rows are locked
    if (row.isBookmarked) return;

    if (suggestionIndex === null) {
      onRowChange(row.ebkpCode, {
        selectedSuggestionIndex: null,
        manualYear: null  // Jahr follows the selected project again
      });
    } else {
      onRowChange(row.ebkpCode, {
        selectedSuggestionIndex: suggestionIndex,
        manualCostPerUnit: null,  // Clear manual costPerUnit, but keep manualAmount
        manualYear: null  // Jahr defaults to the newly selected project's year
      });
    }
  }, [onRowChange]);

  /**
   * Handle manual amount input
   * Level 1: Not allowed (read-only)
   * Level 2+: Allowed (ALWAYS, even if parent has children)
   * Bookmarked rows: Not allowed (locked)
   *
   * UPDATED: Does NOT clear selectedSuggestionIndex (decoupled)
   */
  const handleAmountChange = useCallback((row, value) => {
    // Level 1 is read-only
    if (row.level === 1) return;

    // Bookmarked rows are locked
    if (row.isBookmarked) return;

    onRowChange(row.ebkpCode, {
      manualAmount: value
      // Do NOT clear selectedSuggestionIndex - amount and project are now decoupled
    });
  }, [onRowChange]);

  /**
   * Handle manual cost per unit input
   * Level 1: Not allowed (read-only)
   * Level 2+: Allowed (ALWAYS, even if parent has children)
   * Bookmarked rows: Not allowed (locked)
   *
   * UNCHANGED: Still clears selectedSuggestionIndex
   */
  const handleCostPerUnitChange = useCallback((row, value) => {
    // Level 1 is read-only
    if (row.level === 1) return;

    // Bookmarked rows are locked
    if (row.isBookmarked) return;

    onRowChange(row.ebkpCode, {
      manualCostPerUnit: value,
      selectedSuggestionIndex: null  // Clear project selection when manually entering costPerUnit
    });
  }, [onRowChange]);

  /**
   * Render project dropdown
   * Level 1: Locked (read-only)
   * Level 2+: ALWAYS editable (even if has children)
   * Bookmarked rows: Locked
   *
   * UPDATED: Uses new label format with costPerUnit at beginning
   */
  const renderProjectDropdown = useCallback((row) => {
    // Level 1: Read-only
    if (row.level === 1) {
      return (
        <Tooltip title="Level 1 ist schreibgeschützt - wird automatisch aus Unterebenen berechnet">
          <span style={{ color: '#999', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <LockOutlined />
            Automatisch berechnet
          </span>
        </Tooltip>
      );
    }

    // Bookmarked rows: Locked
    if (row.isBookmarked) {
      return (
        <Tooltip title="Gesperrte Zeile - mit Lesezeichen markiert">
          <span style={{ color: '#52c41a', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <LockOutlined />
            Gesperrt
          </span>
        </Tooltip>
      );
    }

    // Level 2+: Always show editable dropdown
    const hasNoSuggestions = !row.suggestions || row.suggestions.length === 0;

    return (
      <Select
        value={row.selectedSuggestionIndex}
        onChange={(value) => handleProjectChange(row, value)}
        style={{ width: '100%', minWidth: '250px' }}
        size="small"
        placeholder="— Keine Auswahl —"
      >
        <Option key="null-option" value={null}>
          — Keine Auswahl —
        </Option>

        {!hasNoSuggestions && row.suggestions.map((suggestion, index) => (
          <Option key={index} value={index}>
            {formatSuggestionLabel(suggestion)}
          </Option>
        ))}
      </Select>
    );
  }, [handleProjectChange]);

  /**
   * Render amount input
   * Level 1: Read-only (show dash)
   * Level 2+ Parent (not manually edited): Read-only (show dash or sum)
   * Level 2+ (manually edited or leaf): Editable input
   * Bookmarked rows: Read-only (locked)
   *
   * UPDATED: Check if manually edited to determine read-only vs editable
   */
  const renderAmountInput = useCallback((row) => {
    // Level 1: Read-only
    if (row.level === 1) {
      return (
        <span style={{ color: '#999', fontStyle: 'italic' }}>
          —
        </span>
      );
    }

    // Bookmarked rows: Show value but read-only
    if (row.isBookmarked) {
      const amount = row.manualAmount !== null && row.manualAmount !== undefined
        ? row.manualAmount
        : 0;
      return (
        <Tooltip title="Gesperrte Zeile - mit Lesezeichen markiert">
          <span style={{ color: '#52c41a', fontWeight: 500 }}>
            {formatNumber(amount, 2)}
          </span>
        </Tooltip>
      );
    }

    // Level 2+: Check if parent and NOT manually edited
    const manuallyEdited = isManuallyEdited(row);
    const isParent = !row.isLeaf;

    // Parent row that is NOT manually edited: show as read-only
    if (isParent && !manuallyEdited) {
      return (
        <Tooltip title="Automatisch aus Unterebenen berechnet - Eingabe erstellt Inkonsistenz-Warnung">
          <span style={{ color: '#999', fontStyle: 'italic' }}>
            —
          </span>
        </Tooltip>
      );
    }

    // Leaf row OR manually edited parent: show editable input
    const amount = row.manualAmount !== null && row.manualAmount !== undefined
      ? row.manualAmount
      : 0;

    return (
      <InputNumber
        value={amount}
        onChange={(value) => handleAmountChange(row, value)}
        style={{
          width: '100%',
          borderColor: row.hasInconsistency ? '#ff4d4f' : undefined,
          borderWidth: row.hasInconsistency ? '2px' : undefined
        }}
        size="small"
        min={0}
        precision={2}
      />
    );
  }, [handleAmountChange]);

  /**
   * Get the selected suggestion of a row (or null)
   */
  const getSelectedSuggestion = (row) => {
    if (row.selectedSuggestionIndex === null || row.selectedSuggestionIndex === undefined) {
      return null;
    }
    return (row.suggestions && row.suggestions[row.selectedSuggestionIndex]) || null;
  };

  /**
   * Render the Jahr input: the construction year used for the inflation
   * indexing. Defaults to the selected project's Baudatum year; editable to
   * override (e.g. when the year is missing or was parsed incorrectly).
   */
  const renderYearInput = useCallback((row) => {
    if (row.level === 1) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>;
    }

    const effectiveYear = row.manualYear ?? suggestionYear(getSelectedSuggestion(row));

    if (row.isBookmarked) {
      return (
        <Tooltip title="Gesperrte Zeile - mit Lesezeichen markiert">
          <span style={{ color: '#52c41a', fontWeight: 500 }}>
            {effectiveYear || '—'}
          </span>
        </Tooltip>
      );
    }

    const manuallyEdited = isManuallyEdited(row);
    if (!row.isLeaf && !manuallyEdited) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>;
    }

    return (
      <Tooltip
        title="Baujahr für die Teuerung — wird automatisch aus dem gewählten Projekt übernommen. Überschreiben, wenn kein Jahr vorhanden ist oder das Datum falsch geparst wurde."
      >
        <InputNumber
          value={effectiveYear}
          onChange={(value) => onRowChange(row.ebkpCode, { manualYear: value })}
          style={{
            width: '100%',
            borderColor: row.manualYear !== null && row.manualYear !== undefined ? '#b6ce3b' : undefined
          }}
          size="small"
          min={1900}
          max={2100}
          precision={0}
          controls={false}
          placeholder="—"
        />
      </Tooltip>
    );
  }, [onRowChange]);

  /**
   * Render cost per unit input
   * Level 1: Read-only (show dash)
   * Level 2+ Parent (not manually edited): Read-only (show dash)
   * Level 2+ (manually edited or leaf): Editable input
   * Bookmarked rows: Read-only (locked)
   *
   * UPDATED: Check if manually edited to determine read-only vs editable
   */
  const renderCostPerUnitInput = useCallback((row) => {
    const { costPerUnit } = getEffectiveValues(row);

    // Level 1: Read-only
    if (row.level === 1) {
      return (
        <span style={{ color: '#999', fontStyle: 'italic' }}>
          —
        </span>
      );
    }

    // Bookmarked rows: Show value but read-only
    if (row.isBookmarked) {
      return (
        <Tooltip title="Gesperrte Zeile - mit Lesezeichen markiert">
          <span style={{ color: '#52c41a', fontWeight: 500 }}>
            {formatNumber(costPerUnit, 2)} / {row.unit}
          </span>
        </Tooltip>
      );
    }

    // Level 2+: Check if parent and NOT manually edited
    const manuallyEdited = isManuallyEdited(row);
    const isParent = !row.isLeaf;

    // Parent row that is NOT manually edited: show as read-only
    if (isParent && !manuallyEdited) {
      return (
        <Tooltip title="Automatisch aus Unterebenen berechnet - Eingabe erstellt Inkonsistenz-Warnung">
          <span style={{ color: '#999', fontStyle: 'italic' }}>
            —
          </span>
        </Tooltip>
      );
    }

    // Leaf row OR manually edited parent: show editable input
    return (
      <InputNumber
        value={costPerUnit}
        onChange={(value) => handleCostPerUnitChange(row, value)}
        style={{
          width: '100%',
          borderColor: row.hasInconsistency ? '#ff4d4f' : undefined,
          borderWidth: row.hasInconsistency ? '2px' : undefined
        }}
        size="small"
        min={0}
        precision={2}
        suffix={`/${row.unit}`}
      />
    );
  }, [handleCostPerUnitChange]);

  // Check if all top-level rows are included
  const noneTopLevelIncluded = useMemo(() => {
    const topLevelRows = rows.filter(row => row.level === 1);
    return topLevelRows.every(row => row.includedInTotal === false);
  }, [rows]);

  // Rebuilding this array on every render made rc-table re-render every cell,
  // because each cell's render function was a brand new closure. It now only
  // changes when the toggle-all header state does.
  const columns = useMemo(() => [
    {
      title: () => (
        <Tooltip title={noneTopLevelIncluded ? 'Alle einschließen' : 'Alle ausschließen'}>
          <span
            onClick={onToggleAll}
            style={{
              cursor: 'pointer',
              color: noneTopLevelIncluded ? '#ff4d4f' : '#52c41a',
              fontSize: '18px',
              fontWeight: 'bold',
              userSelect: 'none'
            }}
          >
            {noneTopLevelIncluded ? '−' : '+'}
          </span>
        </Tooltip>
      ),
      dataIndex: 'include',
      key: 'include',
      shouldCellUpdate,
      width: 50,
      align: 'center',
      fixed: 'left',
      render: (_, record) => {
        const isIncluded = record.includedInTotal === true;
        const tooltipText = record.isLeaf
          ? (isIncluded ? 'Aus Total ausschließen' : 'In Total einschließen')
          : (isIncluded ? 'Alle untergeordneten Elemente ausschließen' : 'Alle untergeordneten Elemente einschließen');

        return (
          <Tooltip title={tooltipText}>
            <span
              onClick={() => handleToggleInclusion(record)}
              style={{
                cursor: 'pointer',
                color: isIncluded ? '#52c41a' : '#ff4d4f',
                fontSize: '18px',
                fontWeight: 'bold',
                userSelect: 'none'
              }}
            >
              {isIncluded ? '+' : '−'}
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: () => (
        <Tooltip title="Lesezeichen - Markiert abgeschlossene Positionen (gesperrt beim Laden)">
          <CheckOutlined style={{ fontSize: '14px', color: '#52c41a' }} />
        </Tooltip>
      ),
      dataIndex: 'bookmark',
      key: 'bookmark',
      shouldCellUpdate,
      width: 50,
      align: 'center',
      fixed: 'left',
      render: (_, record) => {
        const isBookmarked = record.isBookmarked === true;
        const tooltipText = isBookmarked
          ? 'Lesezeichen entfernen (Zeile entsperren)'
          : 'Lesezeichen setzen (Zeile sperren)';

        return (
          <Tooltip title={tooltipText}>
            <span
              onClick={() => handleToggleBookmark(record)}
              style={{
                cursor: 'pointer',
                color: isBookmarked ? '#52c41a' : '#d9d9d9',
                fontSize: '18px',
                fontWeight: 'bold',
                userSelect: 'none'
              }}
            >
              <CheckOutlined />
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: 'eBKP',
      dataIndex: 'ebkpCode',
      key: 'ebkpCode',
      shouldCellUpdate,
      width: 120,
      fixed: 'left',
      render: (text, record) => {
        return (
          <span
            style={{
              fontWeight: record.level === 1 ? 700 : (record.isLeaf ? 400 : 600),
              color: record.level === 1 ? '#1890ff' : (record.isLeaf ? '#000' : '#1890ff'),
              borderLeft: record.hasInconsistency ? '3px solid #ff4d4f' : (record.isBookmarked ? '3px solid #52c41a' : undefined),
              paddingLeft: (record.hasInconsistency || record.isBookmarked) ? '6px' : undefined
            }}
          >
            {text}
          </span>
        );
      }
    },
    {
      title: 'BKP',
      dataIndex: 'bkpCodes',
      key: 'bkpCodes',
      shouldCellUpdate,
      width: 110,
      fixed: 'left',
      render: (bkpCodes) => {
        if (!bkpCodes || bkpCodes.length === 0) {
          return <span style={{ color: '#ccc' }}>—</span>;
        }
        const text = bkpCodes.join(', ');
        return (
          <Tooltip title={`BKP: ${text}`}>
            <span
              style={{
                color: '#666',
                fontSize: '12px',
                display: 'inline-block',
                maxWidth: '100px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                verticalAlign: 'bottom'
              }}
            >
              {text}
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Element',
      dataIndex: 'name',
      key: 'name',
      shouldCellUpdate,
      width: 300,
      fixed: 'left',
      render: (text, record) => {
        // Calculate indentation based on level
        // Level 1: 0px, Level 2: 40px, Level 3: 80px, Level 4: 120px
        const indentLevel = record.level - 1;
        const indentPx = indentLevel * 40;

        const summary = record.__summary;
        // Whether the subtree holds any reference data from the search. This is
        // a hint only — it must never gate the expander. A search that returns
        // nothing (no matching projects, or no reachable search service) would
        // otherwise lock the whole sheet at the top two levels.
        const hasReference = !summary || summary.withReference > 0;

        return (
          <div
            style={{
              paddingLeft: `${indentPx}px`,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {record.__expandable ? (
              <Tooltip title={record.__expanded
                ? 'Zuklappen'
                : (hasReference ? 'Aufklappen' : 'Aufklappen — keine Referenzdaten aus der Suche')}>
                <span
                  className="estimation-expander"
                  onClick={() => toggleExpanded(record.ebkpCode)}
                >
                  {record.__expanded ? 'v' : '>'}
                </span>
              </Tooltip>
            ) : (
              <span className="estimation-expander-spacer" />
            )}
            <span
              style={{
                fontWeight: record.level === 1 ? 700 : (record.isLeaf ? 400 : 600),
                color: record.level === 1 ? '#1890ff' : (record.isLeaf ? '#000' : '#1890ff')
              }}
            >
              {text}
            </span>
            {record.level === 1 && (
              <Tooltip title="Level 1 ist schreibgeschützt">
                <LockOutlined style={{ color: '#999', fontSize: '12px' }} />
              </Tooltip>
            )}
            {record.isBookmarked && (
              <Tooltip title="Gesperrte Zeile - mit Lesezeichen markiert">
                <LockOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
              </Tooltip>
            )}
            {record.hasInconsistency && (
              <Tooltip title="Warnung: Inkonsistenz zwischen manuellem Wert und Summe der Unterelemente">
                <WarningOutlined style={{ color: '#ff4d4f', fontSize: '12px' }} />
              </Tooltip>
            )}
            {!record.isLeaf && !record.hasInconsistency && !record.isBookmarked && record.level > 1 && (
              <Tooltip title="Dieses Element hat Unterelemente - manuelle Eingabe erstellt Inkonsistenz-Warnung">
                <InfoCircleOutlined style={{ color: '#999', fontSize: '12px' }} />
              </Tooltip>
            )}

            {/* What the collapsed subtree contains, so the user can tell where
                to open without expanding everything first. */}
            {summary && (
              <span className="estimation-group-badges">
                <Tooltip title={`${summary.withReference} von ${summary.positions} Positionen haben Referenzdaten aus der Suche`}>
                  <span className={'estimation-badge' + (summary.withReference === 0 ? ' is-empty' : '')}>
                    {summary.withReference}/{summary.positions}
                  </span>
                </Tooltip>
                {summary.filled > 0 && (
                  <Tooltip title={`${summary.filled} Position${summary.filled === 1 ? '' : 'en'} in dieser Gruppe sind bereits erfasst`}>
                    <span className="estimation-badge is-filled">{summary.filled} erfasst</span>
                  </Tooltip>
                )}
                {summary.warning && !record.hasInconsistency && (
                  <Tooltip title="Inkonsistenz in einem Unterelement">
                    <WarningOutlined style={{ color: '#ff4d4f', fontSize: '12px' }} />
                  </Tooltip>
                )}
                {summary.bookmarked && !record.isBookmarked && (
                  <Tooltip title="Gesperrte Zeile (Lesezeichen) in einem Unterelement">
                    <LockOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                  </Tooltip>
                )}
              </span>
            )}
          </div>
        );
      }
    },
    {
      title: 'Einheit',
      dataIndex: 'unit',
      key: 'unit',
      shouldCellUpdate,
      width: 80,
      align: 'center',
      render: (text, record) => {
        if (record.level === 1) {
          return <span style={{ color: '#999' }}>—</span>;
        }
        return text;
      }
    },
    {
      title: 'Projekt',
      dataIndex: 'projekt',
      key: 'projekt',
      shouldCellUpdate,
      width: 350,
      render: (_, record) => renderProjectDropdown(record)
    },
    {
      title: 'Menge',
      dataIndex: 'amount',
      key: 'amount',
      shouldCellUpdate,
      width: 120,
      align: 'right',
      render: (_, record) => renderAmountInput(record)
    },
    {
      title: 'Jahr',
      dataIndex: 'manualYear',
      key: 'manualYear',
      shouldCellUpdate,
      width: 90,
      align: 'right',
      render: (_, record) => renderYearInput(record)
    },
    {
      title: 'Preis / Einheit',
      dataIndex: 'costPerUnit',
      key: 'costPerUnit',
      shouldCellUpdate,
      width: 150,
      align: 'right',
      render: (_, record) => renderCostPerUnitInput(record)
    },
    {
      title: 'Total (CHF)',
      dataIndex: 'total',
      key: 'total',
      shouldCellUpdate,
      width: 140,
      align: 'right',
      fixed: 'right',
      render: (_, record) => {
        const { total } = getEffectiveValues(record);
        const isIncluded = record.includedInTotal === true;

        return (
          <span
            style={{
              fontWeight: record.level === 1 ? 700 : (record.isLeaf ? 400 : 600),
              fontSize: record.level === 1 ? '15px' : (record.isLeaf ? '13px' : '14px'),
              color: isIncluded ? (record.level === 1 ? '#1890ff' : (record.isLeaf ? '#000' : '#1890ff')) : '#ff7875',
              backgroundColor: record.level === 1 ? '#e6f0ff' : (record.isLeaf ? 'transparent' : '#f0f5ff'),
              padding: record.isLeaf ? '0' : '4px 8px',
              borderRadius: record.isLeaf ? '0' : '4px',
              display: 'inline-block',
              border: record.hasInconsistency ? '2px solid #ff4d4f' : (record.isBookmarked ? '2px solid #52c41a' : undefined)
            }}
          >
            {formatNumber(total, 2)}
          </span>
        );
      }
    }
  ], [
    noneTopLevelIncluded,
    onToggleAll,
    toggleExpanded,
    handleToggleInclusion,
    handleToggleBookmark,
    renderProjectDropdown,
    renderAmountInput,
    renderYearInput,
    renderCostPerUnitInput
  ]);

  const totalRows = rows.length;
  const shownRows = displayRows.length;

  return (
    <div className="estimation-table-wrapper">
      {/* Finding things without scrolling the whole catalogue */}
      <div className="estimation-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          placeholder="eBKP-Code oder Element suchen…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 280 }}
        />

        <span className="estimation-toolbar-count">
          {isFinding
            ? `${matchCount} ${matchCount === 1 ? 'Treffer' : 'Treffer'}, ${shownCount} angezeigt`
            : `${shownRows} von ${totalRows} Zeilen`}
        </span>
      </div>

      <Table
        columns={columns}
        dataSource={displayRows}
        rowKey="ebkpCode"
        pagination={false}
        scroll={TABLE_SCROLL}
        size="small"
        bordered
        rowClassName={rowClassName}
        expandable={TABLE_EXPANDABLE}
      />

      {/* Only as many hits are rendered as the backend's
          ESTIMATION_SEARCH_RESULT_LIMIT allows; the rest on request. */}
      {isFinding && matchCount > shownCount && (
        <div className="estimation-more">
          <Button
            size="small"
            onClick={() => setHitLimit((n) => n + searchResultLimit)}
          >
            weitere {Math.min(searchResultLimit, matchCount - shownCount)} anzeigen
          </Button>
          <span className="estimation-more-hint">
            {shownCount} von {matchCount} Treffern angezeigt
          </span>
        </div>
      )}
    </div>
  );
};

// Keeps the table out of re-renders caused by unrelated parent state
// (saving flags, timestamps, the unsaved-changes banner).
export default React.memo(EstimationTable);